import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import Header from "@/components/header";
import Footer from "@/components/footer";
import PWAInstallButton from "@/components/ui/PWAInstallButton";
import { useEffect, useState } from 'react';
import useMobileDetect from '@/hooks/useMobileDetect';
import AppShell from '@/components/ui/mobile/AppShell';
import MobileHeader from '@/components/ui/mobile/MobileHeader';
import BottomTabBar from '@/components/ui/mobile/BottomTabBar';
import PageTransition from '@/components/ui/mobile/PageTransition';
import { BottomNav } from '@/components/ui/bottom-nav';

// Hàm lấy tiêu đề trang từ đường dẫn
const getPageTitle = (path: string): string => {
  if (path === '/') return 'Giác Ngộ';
  if (path.includes('/courses')) return 'Khóa học';
  if (path.includes('/diagrams')) return 'Sơ đồ';
  if (path.includes('/ai-chat')) return 'Trò chuyện AI';
  if (path.includes('/profile')) return 'Hồ sơ';
  if (path.includes('/learning-dashboard')) return 'Học tập';
  if (path.includes('/quiz')) return 'Trắc nghiệm';
  if (path.includes('/news')) return 'Tin tức';
  if (path.includes('/gamification')) return 'Game';
  return 'Giác Ngộ';
};

// Kiểm tra xem có phải trang chủ hay không
const isHomePage = (path: string): boolean => {
  return path === '/' || path === '';
};

/**
 * Main layout component for the application
 * Contains the header, main content area, and footer
 */
export default function MainLayout() {
  const location = useLocation();
  const { isMobile } = useMobileDetect();
  const [showMobileAppUI, setShowMobileAppUI] = useState(false);
  
  // Kiểm tra xem có đang ở trang chat không - cải thiện cách kiểm tra để bao gồm tất cả các trang chat
  const isChatPage = location.pathname.includes('/ai-chat') || 
                    location.pathname.includes('/chat') || 
                    location.pathname.includes('/expert') ||
                    location.pathname.startsWith('/expert/') ||
                    location.pathname.startsWith('/ai-chat/') ||
                    location.pathname.startsWith('/chat/');
  
  // Kiểm tra xem có phải là thiết bị mobile không và cập nhật UI mode
  useEffect(() => {
    // Không sử dụng UI mobile cho các trang admin
    const isAdminPage = location.pathname.startsWith('/admin');
    
    // Debug log
    console.log('MainLayout useEffect:', { 
      isMobile, 
      pathname: location.pathname, 
      isAdminPage
    });
    
    // LUÔN hiển thị giao diện app khi là thiết bị mobile và không phải trang admin
    if (isMobile && !isAdminPage) {
      setShowMobileAppUI(true);
    } else {
      setShowMobileAppUI(false);
    }
    
  }, [isMobile, location.pathname]);
  
  // Debug log để kiểm tra trạng thái giao diện
  console.log('MainLayout render state:', { 
    showMobileAppUI, 
    isMobile, 
    pathname: location.pathname,
    isChatPage
  });
  
  // Ẩn header và bottom nav khi ở trang chat trên mobile
  const shouldHideHeader = isMobile && isChatPage;
  const shouldHideBottomNav = isMobile && isChatPage;

  // Nếu đang ở chế độ mobile và không phải trang admin, sử dụng giao diện app
  if (showMobileAppUI) {
    return (
      <AppShell
        header={
          // Chỉ hiển thị header nếu không phải trang chat
          !isChatPage ? (
            <MobileHeader
              title={getPageTitle(location.pathname)}
              showBackButton={!isHomePage(location.pathname)}
              showMenuButton={isHomePage(location.pathname)}
              userName={isHomePage(location.pathname) ? "Phật Tử" : undefined}
              variant={isHomePage(location.pathname) ? "welcome" : "default"}
              onOpenSearch={() => window.location.href = '/search'}
            />
          ) : null
        }
        tabBar={<BottomTabBar currentPath={location.pathname} />}
        className="min-h-[100dvh]"
      >
        <PageTransition>
          <Outlet />
        </PageTransition>
      </AppShell>
    );
  }

  // Desktop UI hoặc các trang khác
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <PWAInstallButton />
    </div>
  );
} 